import { Button as HeadlessButton } from '@headlessui/react'
import { clsx } from 'clsx'
import { IconNames } from 'lib/settings/icons'
import type {
  ButtonHTMLAttributes,
  HTMLAttributeAnchorTarget,
  PropsWithChildren,
} from 'react'
import Icon from '../Icon/Icon'

type ButtonVariants = 'primary' | 'secondary' | 'tertiary'

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    PropsWithChildren {
  /**
   * Optional link details.
   */
  link?: {
    href: string
    target?: HTMLAttributeAnchorTarget
  }
  /**
   * The variant of the button (primary, secondary, tertiary)
   */
  variant?: ButtonVariants
  /**
   * Disables the button
   */
  disabled?: boolean
  /**
   * Icon to display on the left of the button
   */
  icon?: IconNames
  /**
   * Optional additional classes to apply to the button
   */
  overrideClassName?: boolean
}

type ButtonClassNames = Record<ButtonVariants, string>

export default function Button({
  link,
  variant = 'primary',
  disabled = false,
  className = '',
  children,
  icon,
  overrideClassName,
  ...props
}: ButtonProps) {
  const buttonClasnames: ButtonClassNames = {
    primary: clsx(
      'min-h-14 md-max:min-h-12 px-4 bg-theme-primary text-white border-theme-primary',
      'hover:bg-theme-accent-2 hover:text-white hover:border-theme-accent-2',
      'active:bg-theme-accent-2 active:text-white active:border-theme-accent-2',
      'focus:bg-theme-primary focus:text-white focus:border-theme-primary',
      'disabled:bg-neutral-600 disabled:text-white disabled:border-neutral-600',
    ),
    secondary: clsx(
      'min-h-14 md-max:min-h-12 px-4 bg-white text-theme-primary border-theme-primary',
      'hover:bg-neutral-200',
      'active:bg-neutral-200',
      'disabled:opacity-75 disabled:bg-white disabled:text-neutral-600 disabled:border-neutral-600',
    ),
    tertiary: clsx(
      'min-h-14 md-max:min-h-12 sm-max:min-h-10 px-7 bg-transparent border-transparent text-theme-primary',
      'hover:bg-neutral-200',
      'active:bg-neutral-200',
      'focus:outline-offset-0',
      'disabled:text-neutral-600',
    ),
  }

  const classNames = overrideClassName
    ? className
    : clsx(
        'type-body-5 rounded-8 border-2 inline-flex items-center justify-center cursor-pointer transition',
        'active:scale-95 disabled:cursor-not-allowed focus:outline-accent-link focus:outline-offset-4 focus:outline-1',
        buttonClasnames[variant],
        className,
      )

  return link?.href ? (
    <a href={link.href} target={link.target} className={classNames}>
      {icon && <Icon icon={icon} className="w-[1.25em] h-[1.25em] mr-2" />}
      {children}
    </a>
  ) : (
    <HeadlessButton className={classNames} disabled={disabled} {...props}>
      {icon && <Icon icon={icon} className="w-[1.25em] h-[1.25em] mr-2" />}
      {children}
    </HeadlessButton>
  )
}
